import { cn } from 'lib/utils';
import { PricingCardProps } from '../../types/pricing.types';
import { PlanPurchaseButton } from './plan-purchase-button';
import { PlanPrice } from './plan-price';
import { PlanFeatures } from './plan-features';
import { ProxyAddon } from './proxy-addon';

export const PricingCard = ({
  planId,
  priceForYear,
  price,
  features,
  isHighlighted,
  onSelect,
  period,
  isUserPlan,
  isUserAuthorized,
  isDowngradeDisabled = false,
  addonPricePerGb,
  proxyTrafficGb,
  onProxyTrafficChange,
  finalPrice,
}: PricingCardProps) => {
  const isFreePlan = price === 0;
  const isBasicPlan = planId === '67b4913a095e3f916635b7fb';
  const isButtonDisabled = isUserPlan || isDowngradeDisabled;
  const shouldShowAddons = !isFreePlan && !isUserPlan && !isDowngradeDisabled;
  const buttonVariant = isHighlighted ? 'default' : 'outline';

  const renderFreePlanFooter = () => {
    if (isUserPlan) {
      return <p className='mt-auto pt-6 text-sm text-ob-text-subtle'>Your current plan</p>;
    }

    if (isUserAuthorized) {
      return null;
    }

    return (
      <div className='mt-auto pt-6'>
        <PlanPurchaseButton
          label='Free'
          variant={buttonVariant}
          isDisabled={false}
          showDowngradeTooltip={false}
          onSelect={onSelect}
        />
      </div>
    );
  };

  const renderPaidPlanFooter = () => {
    let label = 'Try Custom';
    if (isUserPlan) {
      label = 'Current Active Plan';
    } else if (isBasicPlan) {
      label = 'Try Basic';
    }

    return (
      <div className='mt-auto'>
        {shouldShowAddons && (
          <ProxyAddon
            inputId={`proxy-traffic-${planId}`}
            period={period}
            addonPricePerGb={addonPricePerGb}
            proxyTrafficGb={proxyTrafficGb}
            onProxyTrafficChange={onProxyTrafficChange}
          />
        )}
        <div className='mt-6'>
          <PlanPurchaseButton
            label={label}
            variant={buttonVariant}
            isDisabled={isButtonDisabled}
            showDowngradeTooltip={isDowngradeDisabled && !isUserPlan}
            onSelect={onSelect}
          />
        </div>
      </div>
    );
  };

  return (
    <article className={cn(
      'relative flex flex-col rounded-2xl border border-ob-border bg-ob-raised p-[30px] xl:min-h-[548px]',
      isHighlighted && 'border-2 border-ob-primary bg-ob-active p-[29px]',
    )}>
      {isHighlighted && (
        <span className='absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ob-primary px-3.5 py-[5px] text-sm leading-4 text-white'>
          Popular
        </span>
      )}
      <h2 className='mb-[26px] text-[28px] font-semibold leading-none text-ob-text'>
        {isFreePlan ? 'Free' : 'Basic'}
      </h2>
      {!isFreePlan && (
        <PlanPrice price={price} finalPrice={finalPrice} priceForYear={priceForYear} period={period} />
      )}
      <PlanFeatures features={features} />
      {isFreePlan ? renderFreePlanFooter() : renderPaidPlanFooter()}
    </article>
  );
};
